import { metricState, type InsightsMetricState } from './insightsState'
import type { ReviewsGrowthWindow } from './mapReviewsInsights'
import type { ReviewsInsightsGrowthQuery } from '../../reviews.generated'

export type ReviewsAspectKey = 'easeOfUse' | 'customerSupport' | 'requirements' | 'easeOfSetup'

export interface ReviewsAspectScore {
  key: ReviewsAspectKey
  percent: number | null
  categoryPercent: number | null
  delta: number | null
}

export interface ReviewsAspects {
  rows: ReviewsAspectScore[]
  state: InsightsMetricState
}

export type ReviewsMonthlyWindow = Pick<ReviewsGrowthWindow, 'start' | 'end'>

export type ReviewsMonthlyRow = {
  period?: string | null
  reviewsBad?: number | null
  reviewsWorse?: number | null
  reviewsGood?: number | null
  reviewsBetter?: number | null
  reviewsBest?: number | null
  avgEaseOfUse?: number | null
  easeOfUseCount?: number | null
  avgEaseOfSetup?: number | null
  easeOfSetupCount?: number | null
  avgCustomerSupportScore?: number | null
  customerSupportScoreCount?: number | null
  avgRequirementsScore?: number | null
  requirementsScoreCount?: number | null
  categoryAvgEaseOfUse?: number | null
  categoryEaseOfUseCount?: number | null
  categoryAvgEaseOfSetup?: number | null
  categoryEaseOfSetupCount?: number | null
  categoryAvgCustomerSupportScore?: number | null
  categoryCustomerSupportScoreCount?: number | null
  categoryAvgRequirementsScore?: number | null
  categoryRequirementsScoreCount?: number | null
}

type Read = (row: ReviewsMonthlyRow) => number | null | undefined

const ASPECT_SCALE = 7

const ASPECT_FIELDS: { key: ReviewsAspectKey; avg: Read; count: Read; categoryAvg: Read; categoryCount: Read }[] = [
  {
    key: 'easeOfUse',
    avg: (row) => row.avgEaseOfUse,
    count: (row) => row.easeOfUseCount,
    categoryAvg: (row) => row.categoryAvgEaseOfUse,
    categoryCount: (row) => row.categoryEaseOfUseCount,
  },
  {
    key: 'customerSupport',
    avg: (row) => row.avgCustomerSupportScore,
    count: (row) => row.customerSupportScoreCount,
    categoryAvg: (row) => row.categoryAvgCustomerSupportScore,
    categoryCount: (row) => row.categoryCustomerSupportScoreCount,
  },
  {
    key: 'requirements',
    avg: (row) => row.avgRequirementsScore,
    count: (row) => row.requirementsScoreCount,
    categoryAvg: (row) => row.categoryAvgRequirementsScore,
    categoryCount: (row) => row.categoryRequirementsScoreCount,
  },
  {
    key: 'easeOfSetup',
    avg: (row) => row.avgEaseOfSetup,
    count: (row) => row.easeOfSetupCount,
    categoryAvg: (row) => row.categoryAvgEaseOfSetup,
    categoryCount: (row) => row.categoryEaseOfSetupCount,
  },
]

export const ASPECT_KEYS: ReviewsAspectKey[] = ASPECT_FIELDS.map(({ key }) => key)

export const aspectPercent = (score: number | null | undefined): number | null =>
  score == null ? null : Math.round((score / ASPECT_SCALE) * 100)

export const monthlyItem = (growth: ReviewsInsightsGrowthQuery | null | undefined, failed: boolean) =>
  failed ? undefined : growth?.manage?.insights?.results?.[8]

export const monthlyRows = (item: ReturnType<typeof monthlyItem>): ReviewsMonthlyRow[] =>
  item?.status === 200 && item.response?.__typename === 'ManageInsightsRatingMonthlyResponse' ? item.response.rows : []

export const monthsIn = (rows: ReviewsMonthlyRow[], { start, end }: ReviewsMonthlyWindow): ReviewsMonthlyRow[] =>
  rows.filter((row) => row.period != null && row.period >= start && row.period <= end)

const weightedMean = (rows: ReviewsMonthlyRow[], avg: Read, count: Read): number | null => {
  const rated = rows.filter((row) => avg(row) != null && (count(row) ?? 0) > 0)
  const weight = rated.reduce((sum, row) => sum + (count(row) ?? 0), 0)
  return weight ? rated.reduce((sum, row) => sum + (avg(row) ?? 0) * (count(row) ?? 0), 0) / weight : null
}

export const mapReviewsAspects = (
  growth: ReviewsInsightsGrowthQuery | null | undefined,
  window: ReviewsMonthlyWindow,
  failed: boolean,
): ReviewsAspects => {
  const item = monthlyItem(growth, failed)
  const rows = monthlyRows(item)
  const current = monthsIn(rows, window)
  const previous = rows.filter((row) => row.period != null && row.period < window.start)
  const scores = ASPECT_FIELDS.map(({ key, avg, count, categoryAvg, categoryCount }) => {
    const percent = aspectPercent(weightedMean(current, avg, count))
    const before = aspectPercent(weightedMean(previous, avg, count))
    return {
      key,
      percent,
      categoryPercent: aspectPercent(weightedMean(current, categoryAvg, categoryCount)),
      delta: percent == null || before == null ? null : percent - before,
    }
  })
  return { rows: scores, state: metricState(item, scores.filter((score) => score.percent != null).length) }
}
